import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { loginWithDiscord } from '@/store/slices/authSlice';

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((s) => s.auth);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authError  = searchParams.get('error');
  const authDetail = searchParams.get('detail');

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-darkbg flex flex-col items-center justify-center px-4 relative overflow-hidden">
      {/* Layered ambient glows */}
      <div className="absolute inset-0 bg-gold-radial pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_80%,_rgba(201,168,76,0.04)_0%,_transparent_55%)] pointer-events-none" />

      {/* Decorative corner brackets */}
      <div className="absolute top-6 left-6 w-12 h-12 border-t-2 border-l-2 border-gold/20 rounded-tl pointer-events-none" />
      <div className="absolute top-6 right-6 w-12 h-12 border-t-2 border-r-2 border-gold/20 rounded-tr pointer-events-none" />
      <div className="absolute bottom-6 left-6 w-12 h-12 border-b-2 border-l-2 border-gold/20 rounded-bl pointer-events-none" />
      <div className="absolute bottom-6 right-6 w-12 h-12 border-b-2 border-r-2 border-gold/20 rounded-br pointer-events-none" />

      <div className="relative w-full max-w-sm space-y-7 text-center animate-fade-in">
        {/* ── Logo / branding ── */}
        <div className="flex flex-col items-center gap-4">
          {/* Shield icon with glow halo */}
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-gold/10 blur-2xl scale-[2]" />
            <div
              className="relative w-20 h-20 rounded-full
                          bg-gradient-to-b from-darkbg-card to-darkbg-tertiary
                          border border-gold/30 shadow-glow-sm
                          flex items-center justify-center"
            >
              <Shield className="w-9 h-9 text-gold drop-shadow-[0_0_8px_rgba(201,168,76,0.7)]" />
            </div>
          </div>

          <div>
            <h1 className="font-display text-4xl tracking-widest text-gold drop-shadow-[0_0_20px_rgba(201,168,76,0.35)]">
              DARKFELD
            </h1>
            <p className="text-parchment-muted/70 text-sm mt-1 tracking-wide">
              A D&amp;D 3.5 / Pathfinder Community
            </p>
          </div>
        </div>

        {/* ── Ornamental divider ── */}
        <div className="flex items-center gap-3 px-6">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent to-gold/30" />
          <div className="w-1.5 h-1.5 rotate-45 bg-gold/50" />
          <div className="flex-1 h-px bg-gradient-to-l from-transparent to-gold/30" />
        </div>

        {/* ── Error banner ── */}
        {authError && (
          <div className="bg-red-900/40 border border-red-700/60 rounded-lg px-4 py-3 text-left space-y-1">
            <p className="text-red-300 text-sm font-semibold">Sign-in failed</p>
            {authDetail && (
              <p className="text-red-400/80 text-xs font-mono break-all">{authDetail}</p>
            )}
          </div>
        )}

        {/* ── Login card ── */}
        <div className="bg-darkbg-card border border-darkbg-border rounded-xl p-7 space-y-5
                        shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
          <h2 className="font-display text-xl text-parchment">Welcome, Adventurer</h2>
          <p className="text-parchment-muted/80 text-sm leading-relaxed">
            Sign in with your Discord account to access your characters,
            the marketplace, and the community boards.
          </p>

          <button
            onClick={() => dispatch(loginWithDiscord())}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg
                       bg-[#5865F2] hover:bg-[#4752C4] active:bg-[#3c45a5]
                       text-white font-semibold text-base
                       shadow-lg hover:shadow-[0_4px_20px_rgba(88,101,242,0.4)]
                       transition-all duration-150"
          >
            <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.042.028.056a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
            </svg>
            Login with Discord
          </button>
        </div>

        {/* ── Footer note ── */}
        <p className="text-parchment-muted/30 text-xs">
          By signing in you agree to abide by the server rules.{' '}
          <Link
            to="/privacy"
            className="underline hover:text-parchment-muted/60 transition-colors"
          >
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
}
